import { createUuid } from "../../../lib/trace/context";
import { effectiveSummarySource, effectiveSummaryText, isManualSummarySource } from "./itinerarySummaryService";
import { collapseRailwayTransfer, expandRailwayTransfer, resolveRailwayTransferContext, updateRailwayTransferAlternatives } from "./itineraryRailwayTransferService";
import { ITINERARY_TRANSPORT_LABELS as transportModeLabels } from "../config";
import type {
  DrivingResult,
  EnrichedContent,
  IdFactory,
  ItineraryDay,
  ItineraryEditor,
  ItineraryNode,
  ItinerarySegment,
  ItineraryValidationIssue,
  MajorCandidate,
  ParsedDayDraft,
  TransportSelection,
  TransportEndpointChange,
  TransportEndpointDraft,
  TransportToolResultSnapshot,
  TransportWorkspaceBaseline,
  TransportWorkspaceChange,
  TimeSlot,
  WalkingResult,
} from "../types";
import {
  defaultAdjacentTransport,
  setBackupTransports,
  setSelectedTransport,
  rebuildAdjacentSegments,
  isPendingTransportSource,
  validateEditor,
} from "./itineraryEditorService";
import {
  calculatedTransportDurationMinutes,
  formatTransportDuration,
  formatTransportTimeRange,
} from "./itineraryTransportTime";

const busDistanceFormatter = new Intl.NumberFormat("zh-CN", { maximumFractionDigits: 2, useGrouping: false });

/** 大巴距离四舍五入到最多两位小数，原始测算与保存精度保持不变。 */
export function formatDistanceKm(distanceKm: string | number | null | undefined, transportMode: ItinerarySegment["transportMode"]): string | null {
  if (distanceKm === null || distanceKm === undefined || distanceKm === "") return null;
  if (transportMode !== "bus") return String(distanceKm);
  const value = Number(distanceKm);
  return Number.isFinite(value) ? busDistanceFormatter.format(value) : String(distanceKm);
}

export type SegmentRouteSummaryFacts = {
  transportModeLabel: string;
  routeLabel: string | null;
  travelClasses: string[];
  time: string | null;
  duration: string | null;
  distance: string | null;
  backups: string | null;
};
type LongBusReviewFacts = {
  transportMode: ItinerarySegment["transportMode"] | "other";
  isPending?: boolean;
  distanceKm?: string | null;
  durationMinutes?: number | null;
  sourceType?: string | null;
  sourcePayload?: {
    selected?: { distanceKm?: string | null; durationMinutes?: number | null } | null;
  } | null;
};

/** 提取连接器中用于分层展示的结构化交通事实。 */
export function getSegmentRouteSummaryFacts(
  segment: ItinerarySegment,
  options: { hideShortBusDetails?: boolean } = {},
): SegmentRouteSummaryFacts {
  const selected = segment.sourcePayload?.selected;
  const departureAt = selected?.departureAt ?? segment.departureAt;
  const arrivalAt = selected?.arrivalAt ?? segment.arrivalAt;
  const routeLabel = selected
    ? selected.routeCode?.trim() || selected.label.trim()
    : segment.routeCode?.trim();
  const durationMinutes = selected?.durationMinutes ?? segment.durationMinutes;
  const hideDetails = options.hideShortBusDetails && segment.transportMode === "bus"
    && typeof durationMinutes === "number" && Number.isFinite(durationMinutes)
    && durationMinutes > 0 && durationMinutes <= 60;
  const distanceKm = selected?.distanceKm ?? segment.distanceKm;
  const formattedDistanceKm = formatDistanceKm(distanceKm, segment.transportMode);
  return {
    transportModeLabel: transportModeLabels[segment.transportMode],
    routeLabel: routeLabel || null,
    travelClasses: [selected?.seatClass?.trim(), selected?.cabinClass?.trim()].filter((item): item is string => Boolean(item)),
    time: hideDetails ? null : formatTransportTimeRange(departureAt, arrivalAt) || null,
    duration: hideDetails ? null : formatTransportDuration(durationMinutes, "hours-minutes") || null,
    distance: !hideDetails && formattedDistanceKm ? `${formattedDistanceKm} 公里` : null,
    backups: segment.sourcePayload?.backups.length ? `${segment.sourcePayload.backups.length} 条备用` : null,
  };
}

/** 按显示偏好格式化连接器悬浮提示与无障碍说明中的交通事实。 */
export function formatSegmentRouteSummary(
  segment: ItinerarySegment,
  options: { hideTransportMode?: boolean; hideShortBusDetails?: boolean } = {},
): string {
  const facts = getSegmentRouteSummaryFacts(segment, options);
  return [
    options.hideTransportMode ? null : facts.transportModeLabel,
    facts.routeLabel,
    ...facts.travelClasses,
    facts.time,
    facts.duration,
    facts.distance,
    facts.backups,
  ].filter(Boolean).join(" · ");
}

/** 对不适合作为普通接驳直接采信的超长大巴事实给出人工复核提示。 */
export function requiresLongBusReview(segment: LongBusReviewFacts): boolean {
  if (segment.transportMode !== "bus" || isPendingTransportSource(segment.sourceType) || segment.isPending) return false;
  const selected = segment.sourcePayload?.selected;
  const durationMinutes = selected?.durationMinutes ?? segment.durationMinutes;
  const rawDistance = selected?.distanceKm ?? segment.distanceKm;
  const distanceKm = rawDistance === null || rawDistance === undefined || rawDistance === ""
    ? null
    : Number(rawDistance);
  return (durationMinutes !== null && durationMinutes !== undefined && durationMinutes >= 12 * 60)
    || (distanceKm !== null && Number.isFinite(distanceKm) && distanceKm >= 800);
}

/** 原子应用已确认的单日 AI 草稿，并保留跨日及其他日路线。 */
export function applyParsedDayDraft(
  editor: ItineraryEditor,
  dayId: string,
  draft: ParsedDayDraft,
): ItineraryEditor {
  if (draft.day.id !== dayId) throw new Error("AI 建议目标日不一致");
  const next = structuredClone(editor);
  const dayIndex = next.days.findIndex((day) => day.id === dayId);
  if (dayIndex < 0) throw new Error("找不到目标行程日");
  next.days[dayIndex] = structuredClone(draft.day);
  next.segments = [
    ...next.segments.filter(
      (segment) => !(segment.startDayId === dayId && segment.endDayId === dayId),
    ),
    ...structuredClone(draft.segments),
  ];
  next.segments = rebuildAdjacentSegments(next.days, next.segments, createUuid);
  if (validateEditor(next).length) throw new Error("AI 建议未通过校验");
  return next;
}

/** 返回 AI 文案会覆盖的人工内容标签。 */
export function getEnrichmentConflicts(
  editor: ItineraryEditor,
  suggestion: EnrichedContent,
): string[] {
  const conflicts: string[] = [];
  for (const proposed of suggestion.days) {
    const day = editor.days.find((item) => item.id === proposed.dayId);
    if (!day) continue;
    for (const [field, label] of [
      ["dayTitle", `第${day.dayNumber}天标题`],
      ["morningSummary", `第${day.dayNumber}天上午摘要`],
      ["afternoonSummary", `第${day.dayNumber}天下午摘要`],
      ["eveningSummary", `第${day.dayNumber}天晚上摘要`],
      ["summary", `第${day.dayNumber}天总述`],
    ] as const) {
      if (proposed[field] && day[field] && proposed[field] !== day[field]) conflicts.push(label);
    }
    if (proposed.fullDaySummary != null && proposed.fullDaySummary !== effectiveSummaryText(day, "full_day")
      && (effectiveSummaryText(day, "full_day") || isManualSummarySource(effectiveSummarySource(day, "full_day")))) {
      conflicts.push(`第${day.dayNumber}天全天摘要`);
    }
    for (const item of proposed.nodeDescriptions) {
      const node = day.nodes.find((candidate) => candidate.id === item.nodeId);
      if (
        node?.nodeType === "point"
        && node.description
        && node.description !== item.description
      ) conflicts.push(`${node.title}介绍`);
    }
    for (const item of proposed.segmentDescriptions) {
      const segment = editor.segments.find((candidate) => candidate.id === item.segmentId);
      if (segment?.segmentDescription && segment.segmentDescription !== item.segmentDescription) {
        conflicts.push(`路线 ${segment.routeSeq} 说明`);
      }
    }
  }
  return conflicts;
}

/** 按稳定 ID 合并已确认文案，未知 ID 不落草稿。 */
export function applyEnrichedContent(
  editor: ItineraryEditor,
  suggestion: EnrichedContent,
  allowOverwrite: boolean,
): ItineraryEditor {
  if (!allowOverwrite && getEnrichmentConflicts(editor, suggestion).length) {
    throw new Error("AI 文案将覆盖人工内容");
  }
  const next = structuredClone(editor);
  for (const proposed of suggestion.days) {
    const day = next.days.find((item) => item.id === proposed.dayId);
    if (!day) continue;
    for (const field of ["dayTitle", "morningSummary", "afternoonSummary", "eveningSummary", "fullDaySummary", "summary"] as const) {
      if (proposed[field] !== undefined && proposed[field] !== null) {
        day[field] = proposed[field];
        if (field !== "dayTitle") day.legacyCompatibility = false;
        if (field === "morningSummary") day.morningSummarySource = proposed.composedSlots?.includes("morning") ? "ai_composed" : "ai_generated";
        if (field === "afternoonSummary") day.afternoonSummarySource = proposed.composedSlots?.includes("afternoon") ? "ai_composed" : "ai_generated";
        if (field === "eveningSummary") day.eveningSummarySource = proposed.composedSlots?.includes("evening") ? "ai_composed" : "ai_generated";
        if (field === "fullDaySummary") day.fullDaySummarySource = proposed.composedSlots?.includes("full_day") ? "ai_composed" : "ai_generated";
        if (field === "summary") day.summarySource = "ai_generated";
      }
    }
    for (const item of proposed.nodeDescriptions) {
      const node = day.nodes.find((candidate) => candidate.id === item.nodeId);
      if (node?.nodeType === "point") node.description = item.description;
    }
    for (const item of proposed.segmentDescriptions) {
      const segment = next.segments.find((candidate) => candidate.id === item.segmentId);
      if (segment) segment.segmentDescription = item.segmentDescription;
    }
  }
  return next;
}

/** 完整时刻存在时，用实际分钟差归一供应商返回的主备方案时长。 */
export function normalizeTransportSelectionDuration(
  selection: TransportSelection,
): TransportSelection {
  const durationMinutes = calculatedTransportDurationMinutes(
    selection.departureAt,
    selection.arrivalAt,
  );
  return durationMinutes === null
    ? structuredClone(selection)
    : { ...structuredClone(selection), durationMinutes };
}

/** 把主选、备选及受控查询快照一次应用到路线段。 */
export function applyTransportSelection(
  segment: ItinerarySegment,
  selected: TransportSelection,
  backups: TransportSelection[],
  snapshot: TransportToolResultSnapshot,
): ItinerarySegment {
  const normalizedSelected = normalizeTransportSelectionDuration(selected);
  const normalizedBackups = backups.map(normalizeTransportSelectionDuration);
  let sourcePayload = setSelectedTransport(
    { version: 1, backups: [], toolResultSnapshot: structuredClone(snapshot) },
    normalizedSelected,
  );
  sourcePayload = setBackupTransports(sourcePayload, normalizedBackups);
  if (segment.sourcePayload?.railwayInsertionPlacement) {
    sourcePayload.railwayInsertionPlacement = segment.sourcePayload.railwayInsertionPlacement;
  }
  if (segment.sourcePayload?.railwayOvernight) {
    sourcePayload.railwayOvernight = structuredClone(segment.sourcePayload.railwayOvernight);
  }
  return {
    ...structuredClone(segment),
    routeCode: normalizedSelected.routeCode ?? null,
    departureAt: normalizedSelected.departureAt ?? null,
    arrivalAt: normalizedSelected.arrivalAt ?? null,
    durationMinutes: normalizedSelected.durationMinutes ?? null,
    distanceKm: normalizedSelected.distanceKm ?? null,
    sourceType: normalizedSelected.provider,
    sourcePayload,
  };
}

/** 将大巴驾车测算快照写入路线，不创建不存在的主备选。 */
export function applyDrivingMeasurement(
  segment: ItinerarySegment,
  result: DrivingResult,
  queriedAt: string,
  queryKey: string,
): ItinerarySegment {
  return {
    ...structuredClone(segment),
    distanceKm: String(result.distanceKm),
    durationMinutes: calculatedTransportDurationMinutes(
      segment.departureAt,
      segment.arrivalAt,
    ) ?? result.businessDurationMinutes,
    sourceType: result.source,
    sourcePayload: {
      version: 1,
      selected: null,
      backups: [],
      toolResultSnapshot: {
        provider: result.source,
        queriedAt,
        queryKey,
        options: [],
        rawDurationMinutes: result.rawDurationMinutes,
        businessDurationMinutes: result.businessDurationMinutes,
        tollsYuan: String(result.tollsYuan),
        tollDistanceKm: String(result.tollDistanceKm),
        isReference: result.source === "deepseek",
        warnings: result.source === "deepseek"
          ? ["模型估算，仅供参考"]
          : result.tollRoad ? [`收费道路：${result.tollRoad}`] : [],
      },
    },
  };
}

/** 将步行测算快照写入路线，不套用大巴的业务缓冲或收费字段。 */
export function applyWalkingMeasurement(
  segment: ItinerarySegment,
  result: WalkingResult,
  queriedAt: string,
  queryKey: string,
): ItinerarySegment {
  return {
    ...structuredClone(segment),
    distanceKm: String(result.distanceKm),
    durationMinutes: result.businessDurationMinutes,
    sourceType: result.source,
    sourcePayload: {
      version: 1,
      selected: null,
      backups: [],
      toolResultSnapshot: {
        provider: result.source,
        queriedAt,
        queryKey,
        options: [],
        rawDurationMinutes: result.rawDurationMinutes,
        businessDurationMinutes: result.businessDurationMinutes,
        isReference: false,
        warnings: [],
      },
    },
  };
}

/** 更换路线端点时清空只对旧端点成立的交通事实，保留人工说明与备注。 */
export function clearTransportFactsForEndpointChange(segment: ItinerarySegment): ItinerarySegment {
  return {
    ...structuredClone(segment),
    transportSpuId: null,
    transportSkuId: null,
    busSpuId: null,
    busSkuId: null,
    routeCode: null,
    departureAt: null,
    arrivalAt: null,
    distanceKm: null,
    durationMinutes: null,
    sourceType: null,
    sourcePayload: null,
  };
}

/** 手工更改交通方式或班次时清理不匹配的旧主备及工具快照。 */
export function applyManualSegmentEdit(
  current: ItinerarySegment,
  edited: ItinerarySegment,
  { preserveEditedTransportFacts = false }: { preserveEditedTransportFacts?: boolean } = {},
): ItinerarySegment {
  const transportModeChanged = current.transportMode !== edited.transportMode;
  const changed = ([
    "routeType",
    "transportMode",
    "routeCode",
    "departureAt",
    "arrivalAt",
    "durationMinutes",
    "distanceKm",
  ] as const).some((field) => (current[field] ?? null) !== (edited[field] ?? null));
  const next = structuredClone(edited);
  const selected = current.sourcePayload?.selected;
  const restoredFromPersistedSelection = Boolean(
    preserveEditedTransportFacts
    && !transportModeChanged
    && selected
    && ([
      "routeCode",
      "departureAt",
      "arrivalAt",
      "durationMinutes",
      "distanceKm",
    ] as const).every((field) => (
      selected[field] === undefined || selected[field] === null || (edited[field] ?? null) === selected[field]
    )),
  );
  if (transportModeChanged) {
    Object.assign(next, {
      transportSpuId: null,
      transportSkuId: null,
      busSpuId: null,
      busSkuId: null,
    });
    if (!preserveEditedTransportFacts) Object.assign(next, {
      routeCode: null,
      departureAt: null,
      arrivalAt: null,
      distanceKm: null,
      durationMinutes: null,
    });
  }
  return {
    ...next,
    sourceType: changed ? "manual" : edited.sourceType,
    sourcePayload: changed && !restoredFromPersistedSelection ? null : structuredClone(current.sourcePayload),
  };
}

const endpointIdentityFields = [
  "title",
  "nodeType",
  "pointSpuId",
  "stayAreaId",
  "organizationSiteId",
] as const;

const transportFactFields = [
  "transportSpuId",
  "transportSkuId",
  "busSpuId",
  "busSkuId",
  "routeCode",
  "departureAt",
  "arrivalAt",
  "distanceKm",
  "durationMinutes",
  "sourceType",
  "sourcePayload",
] as const;

/** 将节点投影为交通工作区可修改的业务字段，不包含拓扑与排序身份。 */
export function transportEndpointDraft(node: ItineraryNode): TransportEndpointDraft {
  return {
    title: node.title,
    nodeType: node.nodeType,
    pointSpuId: node.pointSpuId ?? null,
    stayAreaId: node.stayAreaId ?? null,
    organizationSiteId: node.organizationSiteId ?? null,
    description: node.description ?? null,
    imageUrl: node.imageUrl ?? null,
    planStartAt: node.planStartAt ?? null,
    planEndAt: node.planEndAt ?? null,
  };
}

/** 捕获交通工作区打开时的完整并发基线。 */
export function captureTransportWorkspaceBaseline(
  editor: ItineraryEditor,
  segment: ItinerarySegment,
): TransportWorkspaceBaseline {
  const currentSegment = editor.segments.find((item) => item.id === segment.id);
  if (!currentSegment || !sameTransportSegmentSnapshot(currentSegment, segment)) {
    throw new Error("交通路线已被其他操作更新，请关闭后重新编辑");
  }
  const endpointIds = new Set([segment.fromNodeId, segment.toNodeId]);
  // 完整中转从任一腿打开都可能更换外端点，必须在打开时捕获整组相邻事实。
  try {
    if (resolveRailwayTransferContext(editor, segment.id)) {
      const existingIds = new Set(editor.days.flatMap((day) => day.nodes.map((node) => node.id)));
      currentSegment.sourcePayload?.railwayTransfer?.nodeIds.forEach((id) => { if (existingIds.has(id)) endpointIds.add(id); });
    }
  } catch {
    // 拆改后的链仍允许普通单腿编辑；整组应用另行拒绝自动收拢。
  }
  const previousEndpointStates = [...endpointIds].map((nodeId) => transportEndpointBaseline(editor, nodeId));
  const previousIncidentSegments = editor.segments
    .filter((item) => item.id !== segment.id && (
      endpointIds.has(item.fromNodeId) || endpointIds.has(item.toNodeId)
    ))
    .map((item) => structuredClone(item));
  const guardedSegmentIds = new Set([segment.id, ...previousIncidentSegments.map((item) => item.id)]);
  const relevantDayIds = new Set([
    segment.startDayId,
    segment.endDayId,
    ...previousEndpointStates.map((state) => state.dayId),
  ]);
  return {
    previousSegment: structuredClone(currentSegment),
    previousSegmentIndex: editor.segments.findIndex((item) => item.id === currentSegment.id),
    previousEditorStartDate: editor.startDate ?? null,
    previousDayDates: editor.days
      .filter((day) => relevantDayIds.has(day.id))
      .map((day) => ({ dayId: day.id, dayDate: day.dayDate ?? null })),
    previousEndpointStates,
    previousIncidentSegments,
    previousIncidentSegmentIndexes: previousIncidentSegments.map((item) => ({
      segmentId: item.id,
      segmentIndex: editor.segments.findIndex((segmentItem) => segmentItem.id === item.id),
    })),
    previousSegmentMemberships: transportSegmentTrackMemberships(editor, guardedSegmentIds),
  };
}

/** 统计更换端点资源后需要清空事实的其他相邻路线数。 */
export function countTransportWorkspaceFactLoss(
  editor: ItineraryEditor,
  change: TransportWorkspaceChange,
): number {
  const changedNodeIds = changedTransportEndpointIds(change.endpointChanges);
  const transferSegmentIds = change.railwayTransfer ? change.previousSegment.sourcePayload?.railwayTransfer?.segmentIds ?? [] : [];
  return editor.segments.filter((segment) => (
    segment.id !== change.segment.id
    && !transferSegmentIds.includes(segment.id)
    && (changedNodeIds.has(segment.fromNodeId) || changedNodeIds.has(segment.toNodeId))
    && hasPersistedTransportFacts(segment)
  )).length;
}

/** 原子应用交通段与端点资源；固定节点 ID，并按新端点类型重置其他受影响交通。 */
export function applyTransportWorkspaceChange(
  editor: ItineraryEditor,
  change: TransportWorkspaceChange,
): ItineraryEditor {
  const currentSegment = editor.segments.find((segment) => segment.id === change.segment.id);
  if (
    !currentSegment
    || editor.segments.findIndex((segment) => segment.id === currentSegment.id) !== change.previousSegmentIndex
    || !sameTransportSegmentSnapshot(currentSegment, change.previousSegment)
  ) {
    throw new Error("交通路线已被其他操作更新，请关闭后重新编辑");
  }
  if (!sameLockedTransportSegmentFields(change.segment, change.previousSegment)) {
    throw new Error("交通工作区不能改变路线拓扑");
  }
  assertTransportDateBaseline(editor, change);
  if (currentSegment.sourcePayload?.railwayOvernight && !change.railwayTransfer) {
    const previous = currentSegment;
    if (change.segment.transportMode !== "rail"
      || change.segment.departureAt?.slice(0, 10) !== previous.departureAt?.slice(0, 10)
      || change.segment.arrivalAt?.slice(0, 10) !== previous.arrivalAt?.slice(0, 10)) {
      throw new Error("这趟火车已经安排了“火车上”住宿。要改乘车日期或换成其他交通，请先调整乘车这几天的安排。");
    }
    change = { ...change, segment: { ...change.segment, sourcePayload: { version: 1, backups: [],
      ...change.segment.sourcePayload, railwayOvernight: structuredClone(currentSegment.sourcePayload.railwayOvernight),
    } } };
  }
  if (change.railwayTransfer && change.previousSegment.sourcePayload?.railwayTransfer) {
    const existingIds = new Set(editor.days.flatMap((day) => day.nodes.map((node) => node.id)));
    const groupEndpointIds = new Set(change.previousSegment.sourcePayload.railwayTransfer.nodeIds.filter((id) => existingIds.has(id)));
    assertTransportEndpointBaselines(editor, change, groupEndpointIds);
    assertIncidentSegmentBaselines(editor, change, groupEndpointIds);
    if (change.endpointChanges.length === 0) {
      const updated = updateRailwayTransferAlternatives(editor, currentSegment.id, change.railwayTransfer);
      if (updated) return updated;
    }
    if (editor.segments.some((segment) => segment.sourcePayload?.railwayOvernight
      && change.previousSegment.sourcePayload?.railwayTransfer?.segmentIds.includes(segment.id))) {
      throw new Error("这段换乘已经安排了“火车上”住宿。要换整段路线，请先调整乘车这几天的安排。");
    }
    const collapsed = collapseRailwayTransfer(editor, currentSegment.id);
    return applyTransportWorkspaceChange(collapsed.editor, {
      ...captureTransportWorkspaceBaseline(collapsed.editor, collapsed.segment),
      segment: clearTransportFactsForEndpointChange(collapsed.segment),
      endpointChanges: change.endpointChanges, railwayTransfer: change.railwayTransfer,
    });
  }
  const normalizedSegment = normalizedTransportWorkspaceSegment(change.segment, change.previousSegment);

  const nodes = new Map(editor.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)));
  const allowedEndpointIds = new Set([currentSegment.fromNodeId, currentSegment.toNodeId]);
  const endpointBaselineChange = change.previousSegment.sourcePayload?.railwayTransfer ? {
    ...change,
    previousEndpointStates: change.previousEndpointStates.filter((state) => allowedEndpointIds.has(state.node.id)),
  } : change;
  assertTransportEndpointBaselines(editor, endpointBaselineChange, allowedEndpointIds);
  const changedNodeIds = changedTransportEndpointIds(change.endpointChanges);
  assertIncidentSegmentBaselines(editor, change, changedNodeIds);
  const targetSegment = changedNodeIds.size > 0
    && sameTransportFacts(normalizedSegment, change.previousSegment)
    ? clearTransportFactsForEndpointChange(normalizedSegment)
    : normalizedSegment;
  const changedEndpointIds = new Set<string>();
  change.endpointChanges.forEach((endpointChange) => {
    const currentNode = nodes.get(endpointChange.nodeId);
    if (
      !currentNode
      || !allowedEndpointIds.has(endpointChange.nodeId)
      || changedEndpointIds.has(endpointChange.nodeId)
    ) {
      throw new Error("交通端点不存在或不属于当前路线");
    }
    changedEndpointIds.add(endpointChange.nodeId);
    assertTransportEndpointDraftKeys(endpointChange.draft);
    if (!sameTransportEndpointDraft(transportEndpointDraft(currentNode), endpointChange.previous)) {
      throw new Error("交通端点已被其他操作更新，请关闭后重新编辑");
    }
  });

  const next = structuredClone(editor);
  const nextNodes = new Map(next.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)));
  change.endpointChanges.forEach((endpointChange) => {
    const node = nextNodes.get(endpointChange.nodeId);
    if (node) {
      const pointChanged = node.nodeType !== endpointChange.draft.nodeType
        || node.pointSpuId !== endpointChange.draft.pointSpuId
        || node.title !== endpointChange.draft.title;
      Object.assign(node, normalizedTransportEndpointDraft(endpointChange.draft));
      // 区域身份由主数据派生；代表站转为实际站名后也须移除区域展示标记。
      if (pointChanged) node.railwayRegionName = null;
    }
  });

  next.segments = next.segments.map((segment) => {
    if (segment.id === change.segment.id) return targetSegment;
    if (changedNodeIds.has(segment.fromNodeId) || changedNodeIds.has(segment.toNodeId)) {
      return defaultTransportSegment(segment, nextNodes);
    }
    return segment;
  });

  const resolvedSegment = next.segments.find((segment) => segment.id === change.segment.id);
  if (
    !resolvedSegment
    || resolvedSegment.routeType !== targetSegment.routeType
    || resolvedSegment.transportMode !== targetSegment.transportMode
    || !sameLockedTransportSegmentFields(resolvedSegment, change.previousSegment)
  ) {
    throw new Error("交通路线身份或方式无效，请关闭后重新编辑");
  }
  const resolvedNodes = new Map(next.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)));
  const resolvedFrom = resolvedNodes.get(resolvedSegment.fromNodeId);
  const resolvedTo = resolvedNodes.get(resolvedSegment.toNodeId);
  if (
    resolvedSegment?.transportMode === "rail"
    && (
      resolvedFrom?.nodeType !== "railway_station"
      || !resolvedFrom.pointSpuId?.trim()
      || resolvedTo?.nodeType !== "railway_station"
      || !resolvedTo.pointSpuId?.trim()
    )
  ) {
    throw new Error("铁路线路两端必须选择火车站点位");
  }
  if (
    resolvedSegment?.transportMode === "air"
    && (
      resolvedFrom?.nodeType !== "airport"
      || !resolvedFrom.pointSpuId?.trim()
      || resolvedTo?.nodeType !== "airport"
      || !resolvedTo.pointSpuId?.trim()
    )
  ) {
    throw new Error("航班线路两端必须选择机场点位");
  }
  const addedIssue = firstAddedValidationIssue(validateEditor(editor), validateEditor(next));
  if (addedIssue) throw new Error(addedIssue.detail);
  // 中转草稿尚未承载单段车次，以整条方案首发、终到时刻复用端点归位；展开后各段保留自己的真实时刻。
  const placementSegment = change.railwayTransfer ? {
    ...resolvedSegment,
    departureAt: change.railwayTransfer.legs[0]?.departureAt,
    arrivalAt: change.railwayTransfer.legs.at(-1)?.arrivalAt,
  } : resolvedSegment;
  // 换端点但未改时刻时只同步端点资源，避免无关重排；新交通段或时刻变更才按交通时刻归位。
  const timingChanged = (change.previousSegment.departureAt ?? null) !== (placementSegment.departureAt ?? null)
    || (change.previousSegment.arrivalAt ?? null) !== (placementSegment.arrivalAt ?? null);
  // 工作区打开时路线已经存在；即使仍是 pending/default_walk 空事实段，也必须保护原连接。
  // 只有新增火车段携带的一次性前插/后插标记允许顺延相邻点位。
  const allowsRailwayInsertionPlacement = resolvedSegment.transportMode === "rail"
    && Boolean(resolvedSegment.sourcePayload?.railwayInsertionPlacement);
  const placed = !hasPersistedTransportFacts(change.previousSegment) || timingChanged
    ? placeTransportEndpointsByTime(next, placementSegment, !allowsRailwayInsertionPlacement)
    : next;
  return change.railwayTransfer ? expandRailwayTransfer(placed, resolvedSegment.id, change.railwayTransfer, allowsRailwayInsertionPlacement) : placed;
}

/** 交通两端按发到时刻归位；无法保持已保存路线的安排留给编辑者调整。 */
function placeTransportEndpointsByTime(editor: ItineraryEditor, segment: ItinerarySegment, preserveConnections: boolean): ItineraryEditor {
  if (!segment.departureAt || !segment.arrivalAt) return editor;
  const placement = !preserveConnections && segment.transportMode === "rail"
    ? segment.sourcePayload?.railwayInsertionPlacement
    : undefined;
  const overnight = segment.departureAt.slice(0, 10) !== segment.arrivalAt.slice(0, 10);
  // 新插入跨夜班次仍归位当天站点；后续日期留在本日晚间末端，不自行迁移日期容器。
  if (segment.startDayId !== segment.endDayId || (overnight && !placement)) {
    return clearRailwayInsertionPlacement(editor, segment.id);
  }
  const next = structuredClone(editor);
  const endpoints = [
    { id: segment.fromNodeId, slot: transportTimeSlot(segment.departureAt) },
    { id: segment.toNodeId, slot: overnight ? "evening" as const : transportTimeSlot(segment.arrivalAt) },
  ];
  if (endpoints.some((endpoint) => !endpoint.slot)) return editor;
  // 路径节点必须来自调整副本，避免失败回退时返回已改时段的节点与旧路线。
  const endpointDay = next.days.find((day) => day.nodes.some((node) => node.id === segment.fromNodeId))
    ?? next.days.find((day) => day.nodes.some((node) => node.id === segment.toNodeId));
  if (!endpointDay) return clearRailwayInsertionPlacement(editor, segment.id);
  const routeSegments = placement && segment.majorId
    ? next.segments.filter((item) => item.majorId === segment.majorId) : next.segments;
  const routeNodeIds = new Set(routeSegments.flatMap((item) => [item.fromNodeId, item.toNodeId]));
  const routeDay = placement && segment.majorId
    ? { ...endpointDay, nodes: endpointDay.nodes.filter((node) => routeNodeIds.has(node.id)) } : endpointDay;
  const routeOrder = linearDayRouteOrder(routeDay, routeSegments);
  if (!routeOrder) return clearRailwayInsertionPlacement(editor, segment.id);
  const fromIndex = routeOrder.findIndex((node) => node.id === segment.fromNodeId);
  const toIndex = routeOrder.findIndex((node) => node.id === segment.toNodeId);
  if (fromIndex < 0 || toIndex < 0 || Math.abs(toIndex - fromIndex) !== 1) return clearRailwayInsertionPlacement(editor, segment.id);
  const slotRank = (slot: TimeSlot) => ["morning", "afternoon", "evening"].indexOf(slot);
  const moveSlots = new Map<string, TimeSlot>();
  if (!overnight && placement) {
    routeOrder.slice(toIndex + 1).forEach((node) => {
      if (slotRank(node.timeSlot) < slotRank(endpoints[1].slot!)) moveSlots.set(node.id, endpoints[1].slot!);
    });
  }
  const currentNodes = editor.days.flatMap((day) => day.nodes);
  if (
    endpoints.every((endpoint) => currentNodes.find((node) => node.id === endpoint.id)?.timeSlot === endpoint.slot)
    && moveSlots.size === 0
  ) return clearRailwayInsertionPlacement(editor, segment.id);
  for (const day of next.days) {
    if (!day.nodes.some((node) => endpoints.some((endpoint) => endpoint.id === node.id) || moveSlots.has(node.id))) continue;
    const ordered = day.id === endpointDay.id
      ? [...routeOrder, ...day.nodes.filter((node) => !routeOrder.some((item) => item.id === node.id))]
      : [...day.nodes].sort((left, right) => left.sortOrder - right.sortOrder);
    for (const endpoint of endpoints) {
      const node = ordered.find((item) => item.id === endpoint.id);
      if (node) node.timeSlot = endpoint.slot!;
    }
    for (const node of ordered) {
      const moveSlot = moveSlots.get(node.id);
      if (moveSlot) node.timeSlot = moveSlot;
    }
    day.nodes = ordered.sort((a, b) => slotRank(a.timeSlot) - slotRank(b.timeSlot));
    for (const slot of ["morning", "afternoon", "evening"]) {
      day.nodes.filter((node) => node.timeSlot === slot).forEach((node, index) => { node.sortOrder = index + 1; });
    }
  }
  next.segments = rebuildAdjacentSegments(next.days, next.segments, createUuid, { preserveStationPairs: false });
  // 编辑时间只改变两端归属；即使邻段尚未填写事实，也不能借归位改连其他点位。
  if ((preserveConnections || overnight || placement === "before") && (next.segments.length !== editor.segments.length
    || editor.segments.some((before) => {
      const after = next.segments.find((item) => item.id === before.id);
      return !after || !sameLockedTransportSegmentFields(before, after);
    }))) return editor;
  const retained = new Set(next.segments.map((item) => item.id));
  if (!retained.has(segment.id) || editor.segments.some((item) => hasPersistedTransportFacts(item) && !retained.has(item.id))) return editor;
  if (firstAddedValidationIssue(validateEditor(editor), validateEditor(next))) return editor;
  return clearRailwayInsertionPlacement(next, segment.id);
}

/** 清除新插入铁路段的一次性前插/后插标记，避免把编辑器过程信息继续带入保存稿。 */
function clearRailwayInsertionPlacement(editor: ItineraryEditor, segmentId: string): ItineraryEditor {
  const segment = editor.segments.find((item) => item.id === segmentId);
  if (!segment?.sourcePayload?.railwayInsertionPlacement) return editor;
  const next = structuredClone(editor);
  const target = next.segments.find((item) => item.id === segmentId);
  if (target?.sourcePayload) {
    const sourcePayload = structuredClone(target.sourcePayload);
    delete sourcePayload.railwayInsertionPlacement;
    target.sourcePayload = sourcePayload;
  }
  return next;
}

/** 仅对完整线性单日路线建立稳定路径顺序，分叉路线不猜测点位先后。 */
function linearDayRouteOrder(day: ItineraryDay, segments: ItinerarySegment[]): ItineraryNode[] | null {
  if (day.nodes.length < 2) return null;
  const nodes = new Map(day.nodes.map((node) => [node.id, node]));
  const edges = segments.filter((segment) => (
    segment.startDayId === day.id && segment.endDayId === day.id
    && nodes.has(segment.fromNodeId) && nodes.has(segment.toNodeId)
  ));
  if (edges.length !== day.nodes.length - 1) return null;
  const nextByNode = new Map<string, string>();
  const incoming = new Set<string>();
  for (const edge of edges) {
    if (nextByNode.has(edge.fromNodeId) || incoming.has(edge.toNodeId)) return null;
    nextByNode.set(edge.fromNodeId, edge.toNodeId);
    incoming.add(edge.toNodeId);
  }
  const starts = day.nodes.filter((node) => !incoming.has(node.id));
  if (starts.length !== 1) return null;
  const ordered: ItineraryNode[] = [];
  const seen = new Set<string>();
  let currentId: string | undefined = starts[0].id;
  while (currentId && !seen.has(currentId)) {
    const node = nodes.get(currentId);
    if (!node) return null;
    ordered.push(node);
    seen.add(currentId);
    currentId = nextByNode.get(currentId);
  }
  return ordered.length === day.nodes.length ? ordered : null;
}

/** 按本地班次钟点划分上午、下午与18点起的晚上。 */
function transportTimeSlot(value?: string | null): TimeSlot | null {
  const time = value?.match(/T([01]\d|2[0-3]):[0-5]\d/)?.[1];
  if (!time) return null;
  return Number(time) < 12 ? "morning" : Number(time) < 18 ? "afternoon" : "evening";
}

/** 归位受既有日程约束时给出明确提示，避免把保存车次误认为排程已完成。 */
export function transportPlacementNotice(segment: ItinerarySegment, from?: ItineraryNode | null, to?: ItineraryNode | null, segments: readonly ItinerarySegment[] = []): string | null {
  if (segment.sourcePayload?.railwayOvernight) return null;
  if (!segment.departureAt || !segment.arrivalAt) return null;
  if (segment.startDayId !== segment.endDayId || segment.departureAt.slice(0, 10) !== segment.arrivalAt.slice(0, 10)) {
    return `跨日到达（${formatTransportTimeRange(segment.departureAt, segment.arrivalAt)}），按普通连线处理，请核对到达日期。`;
  }
  return transportPlacementError(segment, from, to, segments);
}

/** 同日交通的点位时段必须与班次钟点一致；跨日和缺失时刻沿用原提示边界。 */
export function transportPlacementError(segment: ItinerarySegment, from?: ItineraryNode | null, to?: ItineraryNode | null, segments: readonly ItinerarySegment[] = []): string | null {
  if (segment.sourcePayload?.railwayOvernight || !segment.departureAt || !segment.arrivalAt
    || segment.startDayId !== segment.endDayId
    || segment.departureAt.slice(0, 10) !== segment.arrivalAt.slice(0, 10)) return null;
  // 同站中转共用到站节点，等待跨时段不改变其到站归属；只接受仍实际相连的两腿。
  const group = segment.sourcePayload?.railwayTransfer;
  const incoming = segment.transportMode === "rail" && from?.nodeType === "railway_station"
    && group?.nodeIds.length === 3 && group.segmentIds.length === 2
    && group.nodeIds[1] === from.id && group.segmentIds[1] === segment.id
    ? segments.find((item) => item.id === group.segmentIds[0] && item.transportMode === "rail"
      && item.toNodeId === from.id && item.endDayId === segment.startDayId
      && item.sourcePayload?.railwayTransfer?.groupId === group.groupId)
    : undefined;
  const sharesArrivalSlot = incoming?.arrivalAt?.slice(0, 10) === segment.departureAt.slice(0, 10)
    && (calculatedTransportDurationMinutes(incoming?.arrivalAt, segment.departureAt) ?? -1) >= 0;
  const fromSlot = transportTimeSlot(sharesArrivalSlot ? incoming?.arrivalAt : segment.departureAt);
  const toSlot = transportTimeSlot(segment.arrivalAt);
  return (fromSlot && from?.timeSlot !== fromSlot) || (toSlot && to?.timeSlot !== toSlot)
    ? `${transportModeLabels[segment.transportMode]}点位时段与交通时刻不一致，请调整点位时段或交通时刻后再保存。` : null;
}

/** 对完整草稿收集阻断保存的时段错误，保留天次、端点和路线定位。 */
export function validateTransportPlacement(editor: ItineraryEditor): ItineraryValidationIssue[] {
  const nodes = new Map(editor.days.flatMap((day) => day.nodes.map((node) => [node.id, node] as const)));
  const days = new Map(editor.days.map((day) => [day.id, day]));
  return editor.segments.flatMap((segment) => {
    const from = nodes.get(segment.fromNodeId);
    const to = nodes.get(segment.toNodeId);
    const error = transportPlacementError(segment, from, to, editor.segments);
    return error ? [{
      code: "transport-time-slot-mismatch",
      subjectKey: segment.id,
      detail: `第 ${days.get(segment.startDayId)?.dayNumber ?? "?"} 天「${from?.title ?? "起点"} → ${to?.title ?? "终点"}」${error}`,
    }] : [];
  });
}

const lockedTransportSegmentFields = [
  "id",
  "startDayId",
  "endDayId",
  "routeSeq",
  "majorId",
  "fromNodeId",
  "toNodeId",
] as const;

const transportSegmentFieldWhitelist = {
  id: true,
  startDayId: true,
  endDayId: true,
  routeSeq: true,
  majorId: true,
  routeType: true,
  transportMode: true,
  fromNodeId: true,
  toNodeId: true,
  transportSpuId: true,
  transportSkuId: true,
  busSpuId: true,
  busSkuId: true,
  routeCode: true,
  departureAt: true,
  arrivalAt: true,
  distanceKm: true,
  durationMinutes: true,
  segmentDescription: true,
  sourceType: true,
  sourcePayload: true,
  remark: true,
  automaticQueryDepartureSlot: true,
} satisfies Record<keyof ItinerarySegment, true>;

const transportEndpointDraftFieldWhitelist = {
  title: true,
  nodeType: true,
  pointSpuId: true,
  stayAreaId: true,
  organizationSiteId: true,
  description: true,
  imageUrl: true,
  planStartAt: true,
  planEndAt: true,
} satisfies Record<keyof TransportEndpointDraft, true>;

const routeTypeModes: Record<
  ItinerarySegment["routeType"],
  readonly ItinerarySegment["transportMode"][]
> = {
  main_transport: ["rail", "air"],
  charter_bus: ["bus"],
  local_transfer: ["walk"],
};

/** 校验工作区不得借路线草稿修改拓扑、排序或专业归属。 */
function sameLockedTransportSegmentFields(left: ItinerarySegment, right: ItinerarySegment): boolean {
  return lockedTransportSegmentFields.every((field) => (left[field] ?? null) === (right[field] ?? null));
}

/** 对比路线的完整可持久化语义，避免覆盖其他窗口的交通编辑。 */
function sameTransportSegmentSnapshot(left: ItinerarySegment, right: ItinerarySegment): boolean {
  return sameStructuredValue(left, right);
}

/** 按显式字段白名单重建目标路线，并固定身份、顺序、端点和专业归属。 */
function normalizedTransportWorkspaceSegment(
  draft: ItinerarySegment,
  previous: ItinerarySegment,
): ItinerarySegment {
  const allowed = new Set(Object.keys(transportSegmentFieldWhitelist));
  if (Object.keys(draft).some((key) => !allowed.has(key))) {
    throw new Error("交通路线草稿包含不可修改字段");
  }
  const routeType = draft.routeType;
  const transportMode = draft.transportMode;
  if (!(routeType in routeTypeModes) || !routeTypeModes[routeType]?.includes(transportMode)) {
    throw new Error("交通路线类型与交通方式不匹配");
  }
  const next = Object.fromEntries(Object.keys(transportSegmentFieldWhitelist)
    .filter((key) => key in draft)
    .map((key) => [key, structuredClone((draft as unknown as Record<string, unknown>)[key])])) as unknown as ItinerarySegment;
  Object.assign(next, {
    id: previous.id,
    startDayId: previous.startDayId,
    endDayId: previous.endDayId,
    routeSeq: previous.routeSeq,
    routeType,
    transportMode,
    fromNodeId: previous.fromNodeId,
    toNodeId: previous.toNodeId,
  });
  if ("majorId" in previous) next.majorId = previous.majorId;
  else delete next.majorId;
  ([
    "transportSpuId",
    "transportSkuId",
    "busSpuId",
    "busSkuId",
    "routeCode",
    "departureAt",
    "arrivalAt",
    "distanceKm",
    "sourceType",
  ] as const).forEach((field) => {
    if (field in next) next[field] = normalizeOptionalString(next[field]);
  });
  return next;
}

/** 校验查询依赖的行程起始日期与路线相关行程日日期没有并发变化。 */
function assertTransportDateBaseline(editor: ItineraryEditor, change: TransportWorkspaceChange): void {
  const expectedDayIds = new Set([
    change.previousSegment.startDayId,
    change.previousSegment.endDayId,
    ...change.previousEndpointStates.map((state) => state.dayId),
  ]);
  const baselineDayIds = change.previousDayDates.map(({ dayId }) => dayId);
  if (
    baselineDayIds.length !== expectedDayIds.size
    || new Set(baselineDayIds).size !== expectedDayIds.size
    || baselineDayIds.some((dayId) => !expectedDayIds.has(dayId))
    || baselineDayIds.some((dayId) => !editor.days.some((day) => day.id === dayId))
  ) {
    throw new Error("交通日期基线不完整，请关闭后重新编辑");
  }
  const currentDayDates = change.previousDayDates.map(({ dayId }) => {
    const day = editor.days.find((item) => item.id === dayId);
    return { dayId, dayDate: day?.dayDate ?? null };
  });
  if (
    (editor.startDate ?? null) !== change.previousEditorStartDate
    || !sameStructuredValue(currentDayDates, change.previousDayDates)
  ) {
    throw new Error("行程日期已被其他操作更新，请关闭后重新查询");
  }
}

/** 对结构化快照执行键顺序无关的深比较。 */
function sameStructuredValue(left: unknown, right: unknown): boolean {
  return JSON.stringify(canonicalStructuredValue(left)) === JSON.stringify(canonicalStructuredValue(right));
}

/** 返回此次原子修改新增的首个校验问题，既有历史问题不阻塞交通编辑。 */
function firstAddedValidationIssue(
  previous: ReturnType<typeof validateEditor>,
  next: ReturnType<typeof validateEditor>,
) {
  const counts = new Map<string, number>();
  previous.forEach((issue) => {
    const key = validationIssueKey(issue);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return next.find((issue) => {
    const key = validationIssueKey(issue);
    const remaining = counts.get(key) ?? 0;
    if (remaining <= 0) return true;
    counts.set(key, remaining - 1);
    return false;
  });
}

/** 构造校验问题的稳定多重集合键。 */
function validationIssueKey(issue: ReturnType<typeof validateEditor>[number]): string {
  return JSON.stringify([issue.code, issue.detail, issue.subjectKey ?? null]);
}

/** 递归规范对象键顺序，避免字段创建顺序影响并发快照比较。 */
function canonicalStructuredValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalStructuredValue);
  if (!value || typeof value !== "object") return value ?? null;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, item]) => [key, canonicalStructuredValue(item)]));
}

/** 规范可空字符串；拒绝类型断言注入的非字符串值。 */
function normalizeOptionalString(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string") throw new Error("交通路线字段格式无效");
  return value.trim() || null;
}

/** 捕获节点所在行程日及全部专业轨道位置。 */
function transportEndpointBaseline(editor: ItineraryEditor, nodeId: string) {
  const locations = editor.days.flatMap((day) => day.nodes.flatMap((node, nodeIndex) => (
    node.id === nodeId ? [{ day, node, nodeIndex }] : []
  )));
  if (locations.length !== 1) throw new Error("交通端点不存在或位置无效");
  const [{ day, node, nodeIndex }] = locations;
  return {
    node: structuredClone(node),
    dayId: day.id,
    nodeIndex,
    trackMemberships: editor.days.flatMap((item) => item.parallelTracks?.tracks.flatMap((track) => {
      const nodeIndex = track.nodeIds.indexOf(nodeId);
      return nodeIndex < 0 ? [] : [{ dayId: item.id, trackId: track.id, nodeIndex }];
    }) ?? []),
  };
}

/** 捕获指定路线在专业轨道中的位置，确保共享端点操作不改变轨道所有权。 */
function transportSegmentTrackMemberships(editor: ItineraryEditor, segmentIds: Set<string>) {
  return editor.days.flatMap((day) => day.parallelTracks?.tracks.flatMap((track) => track.segmentIds.flatMap(
    (segmentId, segmentIndex) => segmentIds.has(segmentId)
      ? [{ segmentId, dayId: day.id, trackId: track.id, segmentIndex }]
      : [],
  )) ?? []);
}

/** 校验两个固定端点的身份、行程日、排序、时段与专业轨道位置均未并发变化。 */
function assertTransportEndpointBaselines(
  editor: ItineraryEditor,
  change: TransportWorkspaceChange,
  allowedEndpointIds: Set<string>,
): void {
  if (
    change.previousEndpointStates.length !== allowedEndpointIds.size
    || new Set(change.previousEndpointStates.map((state) => state.node.id)).size !== allowedEndpointIds.size
  ) {
    throw new Error("交通端点基线不完整，请关闭后重新编辑");
  }
  change.previousEndpointStates.forEach((previous) => {
    if (!allowedEndpointIds.has(previous.node.id)) {
      throw new Error("交通端点基线不完整，请关闭后重新编辑");
    }
    const current = transportEndpointBaseline(editor, previous.node.id);
    if (
      current.dayId !== previous.dayId
      || current.nodeIndex !== previous.nodeIndex
      || !sameTransportNodeSnapshot(current.node, previous.node)
      || !sameStructuredValue(current.trackMemberships, previous.trackMemberships)
    ) {
      throw new Error("交通端点已被其他操作更新，请关闭后重新编辑");
    }
  });
}

/** 校验所有即将降级的相邻路线及其专业轨道归属仍与打开时一致。 */
function assertIncidentSegmentBaselines(
  editor: ItineraryEditor,
  change: TransportWorkspaceChange,
  changedNodeIds: Set<string>,
): void {
  const previous = change.previousIncidentSegments.filter((segment) => (
    changedNodeIds.has(segment.fromNodeId) || changedNodeIds.has(segment.toNodeId)
  ));
  const current = editor.segments.filter((segment) => (
    segment.id !== change.segment.id
    && (changedNodeIds.has(segment.fromNodeId) || changedNodeIds.has(segment.toNodeId))
  ));
  if (
    previous.length !== current.length
    || previous.some((segment, index) => !sameTransportSegmentSnapshot(segment, current[index]))
  ) {
    throw new Error("相邻交通路线已被其他操作更新，请关闭后重新编辑");
  }
  const previousIndexes = change.previousIncidentSegmentIndexes.filter((item) => (
    previous.some((segment) => segment.id === item.segmentId)
  ));
  if (
    previousIndexes.length !== previous.length
    || previousIndexes.some((item) => editor.segments.findIndex((segment) => segment.id === item.segmentId) !== item.segmentIndex)
  ) {
    throw new Error("相邻交通路线顺序已被其他操作更新，请关闭后重新编辑");
  }
  const guardedIds = new Set([change.segment.id, ...previous.map((segment) => segment.id)]);
  const previousMemberships = change.previousSegmentMemberships.filter((item) => guardedIds.has(item.segmentId));
  const currentMemberships = transportSegmentTrackMemberships(editor, guardedIds);
  if (!sameStructuredValue(previousMemberships, currentMemberships)) {
    throw new Error("专业轨道已被其他操作更新，请关闭后重新编辑");
  }
}

/** 对比完整节点快照，包括固定拓扑字段与可编辑业务字段。 */
function sameTransportNodeSnapshot(left: ItineraryNode, right: ItineraryNode): boolean {
  return sameStructuredValue(left, right);
}

/** 运行时拒绝通过类型断言夹带固定节点字段或未知字段。 */
function assertTransportEndpointDraftKeys(draft: TransportEndpointDraft): void {
  const allowed = new Set<string>(Object.keys(transportEndpointDraftFieldWhitelist));
  const nodeTypes = new Set(["point", "railway_station", "airport", "stay_area", "organization_site", "custom"]);
  if (
    Object.keys(draft).some((key) => !allowed.has(key))
    || typeof draft.title !== "string"
    || !nodeTypes.has(draft.nodeType)
    || !isOptionalString(draft.pointSpuId)
    || !isOptionalString(draft.stayAreaId)
    || !isOptionalString(draft.description)
    || !isOptionalString(draft.imageUrl)
    || !isOptionalString(draft.planStartAt)
    || !isOptionalString(draft.planEndAt)
    || (
      draft.organizationSiteId !== undefined
      && draft.organizationSiteId !== null
      && !Number.isFinite(draft.organizationSiteId)
    )
  ) {
    throw new Error("交通端点草稿包含不可修改字段");
  }
}

/** 判断值是否为可持久化的可空字符串。 */
function isOptionalString(value: unknown): value is string | null | undefined {
  return value === undefined || value === null || typeof value === "string";
}

/** 判断端点业务身份是否发生变化，说明和图片变化不使相邻交通失效。 */
function changedTransportEndpointIds(changes: TransportEndpointChange[]): Set<string> {
  return new Set(changes
    .filter((change) => endpointIdentityFields.some(
      (field) => (change.previous[field] ?? null) !== (change.draft[field] ?? null),
    ))
    .map((change) => change.nodeId));
}

/** 判断提交的交通事实是否仍完全等于工作区打开快照。 */
function sameTransportFacts(left: ItinerarySegment, right: ItinerarySegment): boolean {
  return transportFactFields.every((field) => sameStructuredValue(left[field] ?? null, right[field] ?? null));
}

/** 对比端点打开快照，避免并发编辑被静默覆盖。 */
function sameTransportEndpointDraft(left: TransportEndpointDraft, right: TransportEndpointDraft): boolean {
  return sameStructuredValue(left, right);
}

/** 规范互斥资源引用，避免同一节点同时携带点位、住宿和校区 ID。 */
function normalizedTransportEndpointDraft(draft: TransportEndpointDraft): TransportEndpointDraft {
  const normalized: TransportEndpointDraft = {
    title: draft.title.trim(),
    nodeType: draft.nodeType,
    pointSpuId: normalizeOptionalString(draft.pointSpuId),
    stayAreaId: normalizeOptionalString(draft.stayAreaId),
    organizationSiteId: draft.organizationSiteId ?? null,
    description: draft.description ?? null,
    imageUrl: draft.imageUrl ?? null,
    planStartAt: draft.planStartAt ?? null,
    planEndAt: draft.planEndAt ?? null,
  };
  if (draft.nodeType === "stay_area") {
    return { ...normalized, pointSpuId: null, organizationSiteId: null };
  }
  if (draft.nodeType === "organization_site") {
    return { ...normalized, pointSpuId: null, stayAreaId: null };
  }
  return { ...normalized, stayAreaId: null, organizationSiteId: null };
}

/** 清空受端点替换影响的旧交通事实，并按更新后的端点类型恢复默认交通。 */
function defaultTransportSegment(
  segment: ItinerarySegment,
  nodesById: ReadonlyMap<string, ItineraryNode>,
): ItinerarySegment {
  return {
    id: segment.id,
    startDayId: segment.startDayId,
    endDayId: segment.endDayId,
    routeSeq: segment.routeSeq,
    majorId: segment.majorId ?? null,
    fromNodeId: segment.fromNodeId,
    toNodeId: segment.toNodeId,
    ...defaultAdjacentTransport(
      nodesById.get(segment.fromNodeId),
      nodesById.get(segment.toNodeId),
      segment.startDayId === segment.endDayId,
    ),
  };
}

/** 判断路线是否携带会在端点替换时失效的人工或查询事实。 */
export function hasPersistedTransportFacts(segment: ItinerarySegment): boolean {
  return Boolean(
    segment.transportSpuId
    || segment.transportSkuId
    || segment.busSpuId
    || segment.busSkuId
    || segment.routeCode
    || segment.departureAt
    || segment.arrivalAt
    || segment.distanceKm
    || segment.durationMinutes !== null && segment.durationMinutes !== undefined
    || segment.segmentDescription?.trim()
    || segment.remark?.trim()
    || segment.sourcePayload?.selected
    || segment.sourcePayload?.backups.length
    || segment.sourcePayload?.toolResultSnapshot,
  );
}

/** 删除非最后专业轨道，并清理其独占内部节点和专业路线段。 */
export function deleteParallelTrack(
  editor: ItineraryEditor,
  dayId: string,
  trackId: string,
): ItineraryEditor {
  const next = structuredClone(editor);
  const day = next.days.find((item) => item.id === dayId);
  const tracks = day?.parallelTracks?.tracks;
  if (!day || !tracks) throw new Error("找不到专业轨道日");
  if (tracks.length <= 1) throw new Error("不能删除最后一条专业轨道");
  const removed = tracks.find((track) => track.id === trackId);
  if (!removed) throw new Error("找不到专业轨道");
  const remaining = tracks.filter((track) => track.id !== trackId);
  const remainingNodeIds = new Set(remaining.flatMap((track) => track.nodeIds));
  const removedSegmentIds = new Set(removed.segmentIds);
  next.segments = next.segments.filter((segment) => !removedSegmentIds.has(segment.id));
  next.segments.forEach((segment) => {
    remainingNodeIds.add(segment.fromNodeId);
    remainingNodeIds.add(segment.toNodeId);
  });
  const removedInternalIds = new Set(
    removed.nodeIds.slice(1, -1).filter((nodeId) => !remainingNodeIds.has(nodeId)),
  );
  day.nodes = day.nodes.filter((node) => !removedInternalIds.has(node.id));
  (["morning", "afternoon", "evening"] as const).forEach((slot) => {
    day.nodes
      .filter((node) => node.timeSlot === slot)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .forEach((node, index) => { node.sortOrder = index + 1; });
  });
  day.parallelTracks.tracks = remaining;
  const issues = validateEditor(next);
  if (issues.length) throw new Error(issues[0].detail);
  return next;
}

/** 将普通日一次转换为至少两条使用真实专业 ID 的合法并行轨道。 */
export function convertDayToParallelTracks(
  editor: ItineraryEditor,
  dayId: string,
  majors: MajorCandidate[],
  idFactory: IdFactory,
): ItineraryEditor {
  if (majors.length < 2) throw new Error("至少选择两个专业");
  if (new Set(majors.map((major) => major.id)).size !== majors.length) throw new Error("专业不能重复");
  const next = structuredClone(editor);
  const day = next.days.find((item) => item.id === dayId);
  if (!day || day.nodes.length < 2) throw new Error("专业轨道至少需要共享首尾两个点位");
  if (day.parallelTracks?.tracks.length) throw new Error("当前日已是专业轨道");
  const ordered = orderedDayNodes(day);
  const first = ordered[0].id;
  const last = ordered.at(-1)!.id;
  const specialized: ItinerarySegment[] = [];
  day.parallelTracks = {
    version: 1,
    tracks: majors.map((major, majorIndex) => {
      const nodeIds = majorIndex === 0 ? ordered.map((node) => node.id) : [first, last];
      const segmentIds: string[] = [];
      for (let index = 0; index < nodeIds.length - 1; index += 1) {
        const id = idFactory();
        segmentIds.push(id);
        specialized.push({
          id,
          startDayId: day.id,
          endDayId: day.id,
          routeSeq: index + 1,
          majorId: major.id,
          routeType: "local_transfer",
          transportMode: "walk",
          fromNodeId: nodeIds[index],
          toNodeId: nodeIds[index + 1],
          sourceType: "default_walk",
        });
      }
      return { id: idFactory(), title: major.name, majorId: major.id, nodeIds, segmentIds };
    }),
  };
  const mainMajorId = next.professionSettings?.mainMajorId && majors.some((major) => major.id === next.professionSettings?.mainMajorId)
    ? next.professionSettings.mainMajorId
    : majors[0].id;
  next.professionSettings = {
    mainMajorId,
    majors: [
      ...majors.filter((major) => major.id === mainMajorId),
      ...majors.filter((major) => major.id !== mainMajorId),
    ].map(({ id, name }) => ({ id, name })),
  };
  next.segments = [
    ...next.segments.filter(
      (segment) => !(segment.startDayId === dayId && segment.endDayId === dayId),
    ),
    ...specialized,
  ];
  next.segments = rebuildAdjacentSegments(next.days, next.segments, idFactory);
  if (validateEditor(next).length) throw new Error("专业轨道未通过校验");
  return next;
}

/** 将专业轨道日恢复为可线性编辑的普通日，专业路线统一降级为待确认相邻路线。 */
export function convertParallelTracksToOrdinaryDay(
  editor: ItineraryEditor,
  dayId: string,
  idFactory: IdFactory,
): ItineraryEditor {
  const next = structuredClone(editor);
  const day = next.days.find((item) => item.id === dayId);
  const tracks = day?.parallelTracks?.tracks;
  if (!day || !tracks?.length) throw new Error("当前日不是专业轨道");
  const managedSegmentIds = new Set(tracks.flatMap((track) => track.segmentIds));
  day.parallelTracks = null;
  next.segments = rebuildAdjacentSegments(
    next.days,
    next.segments.filter((segment) => !managedSegmentIds.has(segment.id)),
    idFactory,
  );
  if (!next.days.some((item) => item.parallelTracks?.tracks.length)) next.professionSettings = null;
  const issues = validateEditor(next);
  if (issues.length) throw new Error(issues[0].detail);
  return next;
}

/** 按时段及槽内序号返回单日节点。 */
function orderedDayNodes(day: ItineraryDay) {
  const slots = ["morning", "afternoon", "evening"];
  return [...day.nodes].sort(
    (left, right) => slots.indexOf(left.timeSlot) - slots.indexOf(right.timeSlot) || left.sortOrder - right.sortOrder,
  );
}
